// Program helpers — a "program" is a set of team rosters that share the same
// base name (with the auto-appended " <Boys|Girls> <age/grade/grad_year>"
// suffix stripped) and the same owner_user_id.

/** Strip the multi-team suffix from a roster name to get the program base. */
export function baseProgramName(team) {
    if (!team || !team.name) return "";
    const g = team.gender === "F" ? "Girls" : "Boys";
    const id = team.age_group || team.grade || team.grad_year || "";
    if (id) {
        const suffix = ` ${g} ${id}`;
        if (team.name.endsWith(suffix)) {
            return team.name.slice(0, -suffix.length).trim();
        }
    }
    return team.name.trim();
}

/** Stable group key for a roster — a program is grouped by base name + location +
 *  sport, independent of ownership so a claimed roster and its unclaimed siblings stay
 *  in one card (otherwise claiming/relinquishing one roster splits the program). */
export function programKey(team) {
    const base = baseProgramName(team);
    const loc = `${(team.city || "").toLowerCase()}|${(team.state || "").toLowerCase()}|${(team.sport || "").toLowerCase()}`;
    return `${base}::${loc}`;
}

/** Collapse a flat list of team rosters into program-level groups. */
export function groupIntoPrograms(teams) {
    const map = new Map();
    for (const t of teams || []) {
        const key = programKey(t);
        if (!map.has(key)) {
            map.set(key, {
                key,
                base_name: baseProgramName(t),
                sport: t.sport,
                circuit: t.circuit,
                city: t.city,
                state: t.state,
                zip: t.zip,
                lat: t.lat,
                lng: t.lng,
                logo: t.logo,
                preview_bg: t.preview_bg,
                owner_user_id: t.owner_user_id,
                owner_verified: t.owner_verified,
                rosters: [],
            });
        }
        map.get(key).rosters.push(t);
    }
    // For each program, compute aggregates + a representative team.
    return Array.from(map.values()).map((p) => {
        const genders = new Set(p.rosters.map((r) => r.gender));
        // Include each roster's `ages` array (the backend collapses a program's rosters into
        // one representative carrying the union of ages) so cards show the full range.
        const ageGroups = [...new Set(p.rosters.flatMap((r) => [r.age_group, ...(r.ages || [])]).filter(Boolean))];
        const grades = [...new Set(p.rosters.map((r) => r.grade).filter(Boolean))];
        const gradYears = [...new Set(p.rosters.map((r) => r.grad_year).filter(Boolean))];
        // Best avg rating / total reviews across rosters.
        const totalReviews = p.rosters.reduce((s, r) => s + (r.review_count || 0), 0);
        const weightedAvg = totalReviews > 0
            ? p.rosters.reduce((s, r) => s + (r.avg_rating || 0) * (r.review_count || 0), 0) / totalReviews
            : 0;
        const minDist = p.rosters.reduce((m, r) => {
            if (r.distance_miles == null) return m;
            return m == null ? r.distance_miles : Math.min(m, r.distance_miles);
        }, null);
        // A program is "claimed" if any roster is a verified claim; surface that owner.
        const claimedRoster = p.rosters.find((r) => r.owner_verified) || null;
        return {
            ...p,
            owner_user_id: claimedRoster ? claimedRoster.owner_user_id : null,
            owner_username: claimedRoster ? claimedRoster.owner_username : null,
            owner_verified: !!claimedRoster,
            genders: [...genders],
            age_groups: ageGroups,
            grades,
            grad_years: gradYears,
            roster_count: p.rosters.length,
            avg_rating: Math.round(weightedAvg * 10) / 10,
            review_count: totalReviews,
            distance_miles: minDist,
            representative: p.rosters[0],
        };
    });
}
